<template>
  <v-container fluid justify-start>
    <loading
      slot="activator"
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="filteredUsers"
      :loading="isLoading"
      :search="search"
      :custom-filter="customFilter"
      :no-data-text="noDataText"
      item-key="id"
      :loading-text="getExpression('Alert', 'LoadingItems')"
      :footer-props="{
        'items-per-page-text': getExpression('Label', 'RowsPerPage')
      }"
      class="elevation-2"
      @click:row="refreshExtraFields"
      >
      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate />

      <template slot="actions" slot-scope="row">
        <!-- We use @click.stop here to prevent a 'row-clicked' event from also happening -->
        <b-button size="sm" @click.stop="row.toggleDetails">
            {{ row.detailsShowing ? 'Hide' : 'Show' }} Details
        </b-button>
    </template>
    
      <template v-slot:top>

        <!-- Header Toolbar -->
        <v-toolbar dense flat dark :color="toolbarColor">
          <app-back-button v-bind:color="toolbarColorDark" />
          <v-toolbar-title :class="titleClass">{{ title }}</v-toolbar-title>

          <v-spacer />

          <v-toolbar-items>

            <!-- Edit Dialog -->
            <v-dialog v-model="editDialog" :max-width="dialogWidth" :persistent="true">

              <template v-if="canEdit" v-slot:activator="{ on }">
                <v-btn v-if="isSmallDevice"
                  small
                  slot="activator"
                  :disabled="isLoading"
                  :color="toolbarColorDark"
                  class="mb-1 mt-0 mr-0 elevation-0"
                  v-on="on"
                  >New
                </v-btn>
                <v-btn v-else
                  slot="activator"
                  :disabled="isLoading"
                  :color="toolbarColorDark"
                  class="mb-1 mt-0 mr-0 elevation-0"
                  v-on="on"
                  >Create User
                </v-btn>
              </template>

              <v-card>
                <v-toolbar dense dark flat :color="toolbarColor">
                  <v-toolbar-title>
                    <v-icon class="mr-2">edit</v-icon>
                      {{ formTitle }}
                  </v-toolbar-title>
                  <template v-if="editedUser.id !== null && isAdmin">

                    <v-spacer />

                    <v-toolbar-items>
                      <v-btn class="elevation-0" :color="toolbarColorDark" @click="unblock()">Unblock</v-btn>
                    </v-toolbar-items>
                  </template>
                </v-toolbar>

                <v-card-text class="pb-0 px-2">
                  <v-container grid-list-md>
                    <form>
                      <v-row no-gutters v-if="editedIndex >= 0">
                        
                          <span class="mb-2">
                            Created: <app-date :date="editedUser.createDate" />
                          </span>
                        
                      </v-row>
                      <!-- Row 1 (Picture/Email) -->
                      <v-row no-gutters>
                        <!-- Picture -->
                        <template v-if="editedIndex >= 0">
                          <v-col cols="12" sm="2">
                            <v-img :src="editedUser.picture" width="90" />
                          </v-col>
                        </template>

                        <!-- Email -->
                        <v-col cols="12" :sm="editedIndex >= 0 ? '10' : '12'">
                          <v-text-field
                            v-model="editedUser.email"
                            :label="getExpression('Label', 'Email')"
                            v-validate="'required|email'"
                            :error-messages="errors.collect('email')"
                            data-vv-name="email"
                            @keydown.space.prevent
                            hide-details="auto"
                            ref="email"
                            :class="editedIndex >= 0 ? 'ml-3 mb-6' : 'mb-3'"
                          />
                        </v-col>
                      </v-row>

                      <!-- Row 2 (Name) -->
                      <v-row no-gutters>
                        <v-col cols="12" sm="12">
                          <v-text-field
                            v-model="editedUser.name"
                            :label="getExpression('Label', 'Name')"
                            v-validate="'required|max:50|alpha_spaces'"
                            :error-messages="errors.collect('name')"
                            data-vv-name="name"
                            hide-details="auto"
                            ref="name"
                            class="mb-4"
                          />
                        </v-col>
                      </v-row>

                      <!-- Row 3 (Display Name & Language) -->
                      <v-row no-gutters>
                        <v-col cols="12" sm="6">
                          <v-text-field
                            v-model="editedUser.nickname"
                            :label="getExpression('Label', 'DisplayName')"
                            v-validate="'max:20'"
                            :error-messages="errors.collect('nickname')"
                            data-vv-name="nickname"
                            class="mb-2"
                          />
                        </v-col>
                        <v-col cols="12" sm="6">
                          <v-select
                            v-model="editedUser.languageId"
                            :items="languagedLanguages"
                            item-value="id"
                            item-text="description"
                            :label="getExpression('Label', 'Language')"
                            class="ml-2"
                          />
                        </v-col>
                      </v-row>

                      <!-- Row 4 (Role) -->
                      <v-row v-if="!hideRole" no-gutters>
                        <v-col cols="12" sm="12">
                          <v-select
                            v-model="editedUser.functionalRole"
                            :label="getExpression('Label', 'Role')"
                            :items="functionalRolesForEdit"
                            v-validate="'required'"
                            :error-messages="errors.collect('role')"
                            data-vv-name="role"
                            hide-details="auto"
                            class="mb-5"
                          />
                        </v-col>
                      </v-row>

                      <!-- Row 5 & 6 (Domains) -->
                      <v-row no-gutters>
                        <!-- Assigned Domains -->
                        <v-col cols="12" sm="12">
                          <domain-selector
                            v-if="showDomains"
                            v-model="editedUser.domainIds"
                            :options="selectorDomains"
                            :label="getExpression('Label', 'AssignedDomains')"
                            :multiple="allowMultipleDomains"
                            :close="isFromKioskDashboard"
                            :chipColor="getRoleColor(editedUser)"
                            v-validate="domainsRule"
                            :error="errors.first('domain')"
                            data-vv-name="domain"
                          />
                        </v-col>
                      </v-row>

                      <!-- Row 7 & 8 (Kiosks) -->
                      <v-row no-gutters>
                        <!-- Assigned Kiosks -->
                        <v-col cols="12" sm="12">
                          <kiosk-selector
                            v-if="showKiosks"
                            v-model="editedUser.kioskIds"
                            :options="selectorKiosks"
                            :label="getExpression('Label', 'AssignedKiosks')"
                            :multiple="allowMultipleKiosks"
                            :chipColor="getRoleColor(editedUser)"
                            v-validate="kiosksRule"
                            :error="errors.first('kiosk')"
                            data-vv-name="kiosk"
                          />
                        </v-col>
                      </v-row>

                      <!-- Rows 9 & 10 (Residents) -->
                      <v-row no-gutters>
                        <!-- Assigned Residents -->
                        <v-col cols="12" sm="12">
                          <resident-selector
                            v-if="showResidents"
                            v-model="editedUser.residentIds"
                            :options="selectorResidents"
                            :label="getExpression('Label', 'AssignedResidents')"
                            :chipColor="getRoleColor(editedUser)"
                            v-validate="residentsRule"
                            :error="errors.first('resident')"
                            data-vv-name="resident"
                            :showKioskColumn="true"
                            :disabled="disableResidentSelector"
                            :showTooltipButton="showResidentTooltipButton"
                            :tooltipText="residentInfoText"
                          />
                        </v-col>
                      </v-row>

                      <!--Default Resident -->
                      <template v-if="showDefaultResident">
                        <v-row no-gutters>
                          <v-col cols="12" sm="12">
                            <v-select
                              v-model="editedUser.defaultResidentId"
                              :items="defaultResidents"
                              item-value="id"
                              item-text="name"
                              hide-details="auto"
                              :label="getExpression('Label', 'DefaultResident')"
                              class="mb-3"
                            />
                          </v-col>
                        </v-row>
                      </template>

                    </form>
                  </v-container>
                </v-card-text>

                <v-card-actions class="pb-3">

                  <!-- Resend Invitation Email -->
                  <v-row no-gutters v-if="showResendInvitation" class="ml-1">
                    <v-col cols="12" sm="2" class="text-right">
                      <v-btn text small @click="resendInvitationEmail" color="blue">
                        <v-icon class="mr-2">send</v-icon>
                        {{ getExpression('Button', 'ResendInvitation') }}
                      </v-btn>
                    </v-col>
                  </v-row>

                  <!-- Password -->
                  <v-row no-gutters v-if="showPassword">
                    <v-col cols="12" sm="5">
                      <v-text-field
                        label="Password"
                        v-model="fixedPassword"
                        readonly
                        hide-details
                        class="mb-4 ml-2"
                        >
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" sm="1" class="text-right">
                      <v-btn
                        icon
                        v-clipboard:copy="fixedPassword"
                        v-clipboard:success="onCopy"
                        class="mt-2"
                        >
                        <v-icon>content_copy</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>

                  <v-spacer />
                  
                  <v-btn @click="closeDialog" :loading="isSaving" class="rounded-0" large>{{ getExpression('Button', 'Cancel') }}</v-btn>
                  <v-btn :color="appColor_Dark" dark @click="save" :loading="isSaving" class="rounded-0 ml-4" large>{{ getExpression('Button', 'Save') }}</v-btn>
                </v-card-actions>

              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Filters -->
        <template v-if="isAdminMode">
          <!-- Search -->
          <v-col cols="12" md="12">
            <v-text-field
              v-model="search"
              append-icon="search"
              :label="getExpression('Label', 'Search')"
              single-line
              hide-details
              :disabled="isLoading"
              clearable
              class="mb-3"
            />
          </v-col>

          <v-expansion-panels class="mb-2" v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-header class="py-0" @click="filterPanelClick()">
                <span>Filters</span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
    
                <v-divider class="mb-3" />
    
                <v-row no-gutters class="mx-3 mt-1">
      
                  <!-- Kiosk Type Dropdown -->
                  <v-col cols="12" md="2">
                    <v-select
                      v-model="kioskTypeId"
                      :items="kioskTypesForFilter"
                      item-value="id"
                      item-text="name"
                      :label="getExpression('Label', 'Type')"
                      :placeholder="getExpression('Label', 'SelectType')"
                      :disabled="isLoading"
                      class="mr-3"
                    />
                  </v-col>
      
                  <!-- Kiosks Dropdown -->
                  <v-col cols="12" md="4">
                    <kiosk-combobox
                      v-model="kioskId"
                      :options="kiosksForCombo"
                      :label="getExpression('Label', 'Kiosk')"
                      :placeholder="getExpression('Label', 'SelectKiosk')"
                      :disabled="disableKioskFilter"
                      class="mr-3"
                    />
                  </v-col>
      
                  <!-- Residents Dropdown -->
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="residentId"
                      :items="residentsForFilter"
                      item-value="id"
                      item-text="name"
                      :label="getExpression('Label', 'Resident')"
                      :disabled="disableResidentFilter"
                      class="mr-3"
                    />
                  </v-col>
                
                  <!-- Role -->
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="functionalRole"
                      :items="rolesWithAll"
                      :label="getExpression('Label', 'Role')"
                      placeholder="Role"
                      :disabled="isLoading"
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

        </template>

        <template v-else>
          <v-row no-gutters class="mx-3 mt-2">
            <!-- Role -->
            <template v-if="!hideRoleFilter">
              <v-col cols="12" md="3">
                <v-select
                  v-model="functionalRole"
                  :items="rolesWithAll"
                  :label="getExpression('Label', 'Role')"
                  placeholder="Role"
                  :disabled="isLoading"
                  class="mr-3"
                />
              </v-col>
            </template>

            <!-- Search -->
            <v-col cols="12" :md="searchColWidth">
              <v-text-field
                v-model="search"
                append-icon="search"
                :label="getExpression('Label', 'Search')"
                single-line
                hide-details
                :disabled="isLoading"
                clearable
              />
            </v-col>
          </v-row>
        </template>
      </template>

      <template v-slot:item.name="{ item }">
        <span class="text-left" style="white-space: nowrap">
          <span @click="!disableEdit(item) ? edit(item) : ''">
            <span :style="!disableEdit(item) ? appStyle_HyperLink : ''">{{ item.name }}</span>
            <span v-if="isSignedOnUser(item)" class="text-left pl-2">(You)</span>
            <v-icon v-if="!disableEdit(item)" small style='cursor: pointer' class="ml-2">edit</v-icon>
          </span>
        </span>
      </template>

      <template v-slot:item.email="{ item }">
        <span class="text-left" style="white-space: nowrap">
          <span v-if="item.isEmailVerified !== null">
            <v-icon v-if="item.isEmailVerified" small color="green" class="mr-1">done</v-icon>
            <v-icon v-else small color="red" class="mr-1">close</v-icon>
          </span>
          <span>{{ item.email }}</span>
        </span>
      </template>

      <template v-slot:item.picture="{ item }">
        <span class="text-center">
          <v-img width="50" height="50" :src="item.picture"></v-img>
        </span>
      </template>

      <template v-slot:item.functionalRole="{ item }">
        <span class="text-center">
          <v-chip v-if="isAdminMode && !isOrphaned(item)" dark small :color="getRoleColor(item)">
            {{ getRoleCode(item) }}
          </v-chip>
          <v-chip outlined v-else small>
            {{ !isOrphaned(item) ? item.functionalRole : 'None' }}
          </v-chip>
        </span>
      </template>

      <template v-slot:item.assignedDomains="{ item }">
        <span>
          <v-list v-for="(d, idx) in item.assignedDomains" :key="d.id" class="pa-0">
            <span style="white-space: pre">
              <span v-if="idx < item.assignedDomains.length - 1">{{ `${d.name},` }}</span>
              <span v-else>{{ d.name }}</span>
            </span>
          </v-list>
        </span>
      </template>

      <template v-slot:item.assignedKiosks="{ item }">
        <span>
          <v-list v-for="(k, idx) in item.assignedKiosks" :key="k.id" class="pa-0">
            <span style="white-space: pre">
              <span v-if="idx < item.assignedKiosks.length - 1">{{ `${k.serialNumber},` }}</span>
              <span v-else>{{ k.serialNumber }}</span>
            </span>
          </v-list>
        </span>
      </template>

      <template v-slot:item.assignedResidents="{ item }">
        <span>
          <v-list v-for="(r, idx) in item.assignedResidents" :key="r.id" class="pa-0">
            <span style="white-space: pre">
              <span v-if="idx < item.assignedResidents.length - 1">{{ `${r.name},` }}</span>
              <span v-else>{{ r.name }}</span>
            </span>
          </v-list>
        </span>
      </template>

      <template v-slot:item.lastLogin="{ item }">
        <span class="text-left"><app-date :date="item.lastLogin" /></span>
      </template>

      <template v-slot:item.action="{ item }">
        <template v-if="isAdminMode">
          <v-icon v-if="!disableDelete(item)" @click="remove(item)"
            >delete
          </v-icon>
          <v-icon v-else color="grey lighten-1">delete</v-icon>
        </template>
        <template v-else>
          <v-icon v-if="!disableDelete(item)" @click="unlink(item)"
            >remove_circle
          </v-icon>
          <v-icon v-else color="grey lighten-1">remove_circle</v-icon>
        </template>
      </template>
    </v-data-table>

    <!-- Invitation Email Snackbar-->
    <v-snackbar v-model="invitationSnackbar" :timeout="3000">
      An invitation email has been sent!
      <v-btn dark text @click="invitationSnackbar = false" :color="appColor_Medium">
        Close
      </v-btn>
    </v-snackbar>

    <!-- Clipboard Copy Snackbar-->
    <v-snackbar v-model="copySnackbar" :timeout="2000">
      Copied!
      <v-btn dark text @click="copySnackbar = false" :color="appColor_Medium">
        Close
      </v-btn>
    </v-snackbar>

    <!-- Invitation Email Snackbar-->
    <v-snackbar v-model="invitationSnackbar" :timeout="3000">
      An invitation email has been sent!
      <v-btn dark text @click="invitationSnackbar = false" :color="appColor_Medium">
        Close
      </v-btn>
    </v-snackbar>

    <!-- Unblock User Snackbar-->
    <v-snackbar v-model="unblockSnackbar" :timeout="3000">
      {{ unblockSnackbarText }}
      <v-btn dark text @click="unblockSnackbar = false" :color="appColor_Medium">
        Close
      </v-btn>
    </v-snackbar>

  </v-container>
</template>

<script>
import appService from '@/app.service'
import { mapGetters } from 'vuex'
import DomainSelector from '@/components/DomainSelector'
import KioskSelector from '@/components/KioskSelector'
import ResidentSelector from '@/components/ResidentSelector'
import KioskCombobox from '@/components/KioskCombobox'
import AppDate from '@/components/App/AppDate'
import AppBackButton from '@/components/App/AppBackButton'
import { AppColor, AppStyle, ImageUri, UserRole, KioskTypeId, LanguageId, UserIds, Context } from '@/config.js'

export default {
  name: 'UserManager',
  components: {
    DomainSelector,
    KioskSelector,
    ResidentSelector,
    KioskCombobox,
    AppDate,
    AppBackButton
  },
  props: {
    canEdit: {
      type: Boolean,
      default: true
    },
    canDelete: {
      type: Boolean,
      default: true
    },
    canChangeRole: {
      type: Boolean,
      default: false
    },
    showAssignedResidents: {
      type: Boolean,
      default: false
    },
    functionalRoles: {
      type: Array,
      default: null
    },
    context: {
      type: String,
      default: 'admin'
    },
    toolbarColor: {
      type: String,
      default: AppColor.Medium
    },
    toolbarColorDark: {
      type: String,
      default: AppColor.Dark
    }
  },
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    return {
      // colors
      appColor_Medium: AppColor.Medium,
      appColor_Dark: AppColor.Dark,
      appColor_DeleteButton: AppColor.DeleteButton,
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,
      appColor_GenieLight: AppColor.KioskTypeGenieLight,
      appColor_Genie: AppColor.KioskTypeGenie,
      appColor_AbbyLight: AppColor.KioskTypeAbbyLight,
      appColor_Abby: AppColor.KioskTypeAbby,
      appColor_Resident: AppColor.ResidentLight,
      appColor_ResidentDark: AppColor.ResidentDark,

      // role colors
      appColor_RoleAdmin: AppColor.RoleAdmin,
      appColor_RoleDomainManager: AppColor.RoleDomainManager,
      appColor_RoleKioskManager: AppColor.RoleKioskManager,
      appColor_RoleResidentManager: AppColor.RoleResidentManager,
      appColor_RoleGuest: AppColor.RoleGuest,

      // styles
      appStyle_HyperLink: AppStyle.HyperLink,

      // image uri
      imageUri_NoAvatar: ImageUri.NoAvatar,

      // base roles
      role_Guest: UserRole.Guest,
      role_Manager: UserRole.Manager,
      role_Admin: UserRole.Admin,

      // functional roles
      functionalRole_NoneAssigned: 'None Assigned',

      // kiosk types
      kioskTypeId_Genie: KioskTypeId.Genie,
      kioskTypeId_Abby: KioskTypeId.Abby,
      
      // user ids
      superAdmins: UserIds.SuperAdmin,
      
      // contexts
      context_Genie: Context.Genie,
      context_Abby: Context.Abby,

      // tooltip
      residentTooltip: false,

      panel: null,

      isLoading: true,
      isSaving: false,
      isDeleting: false,
      search: '',
      editDialog: false,
      users: [],
      functionalRole: null,
      residents: [],
      kiosks: [],
      kiosksForCombo: [],
      kioskId: null,
      residentsForFilter: [],
      residentId: 0,
      kioskTypesForFilter: [],
      kioskTypeId: 0,
      facilities: [],
      domains: [],
      fixedPassword: '@mbient1',
      copySnackbar: false,
      invitationSnackbar: false,
      unblockSnackbar: false,

      editedIndex: -1,
      editedUser: {
        id: null,
        name: null,
        email: null,
        nickname: null,
        picture: this.imageUri_NoAvatar,
        domainIds: null,
        extraneousDomainIds: null,
        kioskIds: null,
        extraneousKioskIds: null,
        residentIds: null,
        extraneousResidentIds: null,
        defaultResidentId: null,
        defaultResidentName: null,
        role: UserRole.Guest,
        functionalRole: null,
        isAdmin: false,
        createDate: null
      },
      defaultUser: {
        id: null,
        name: null,
        email: null,
        nickname: null,
        languageId: null,
        picture: this.imageUri_NoAvatar,
        domainIds: null,
        extraneousDomainIds: null,
        kioskIds: null,
        extraneousKioskIds: null,
        residentIds: null,
        extraneousResidentIds: null,
        defaultResidentId: null,
        defaultResidentName: null,
        role: UserRole.Guest,
        functionalRole: null,
        isAdmin: false,
        createDate: null
      },
      restoreUser: {
        id: null,
        name: null,
        email: null,
        nickname: null,
        languageId: null,
        picture: this.imageUri_NoAvatar,
        domainIds: null,
        extraneousDomainIds: null,
        kioskIds: null,
        extraneousKioskIds: null,
        residentIds: null,
        extraneousResidentIds: null,
        defaultResidentId: null,
        defaultResidentName: null,
        role: UserRole.Guest,
        functionalRole: null,
        isAdmin: false,
        createDate: null
      }
    }
  },
  computed: {
    ...mapGetters([
      'profile',
      'activeResident',
      'activeResidentKioskItem',
      'activeKioskItem',
      'isAdmin',
      'isDomainManager',
      'isKioskManager',
      'isResidentManager',
      'languages',
      'routeStack',
      'locale']),
    noDataText () {
      if (this.isLoading) return this.getExpression('Alert', 'OneMoment')

      if (this.filteredUsers.length === 0) {
        if(this.isFromResidentDashboard && !this.isNullOrUndefined(this.activeResident)) {
          return (
            this.activeKioskTypeId === this.kioskTypeId_Genie
              ? this.getExpression('Alert', 'NoContactsAssigned').replace('{0}', this.activeResident.name)
              : this.getExpression('Alert', 'NoFacilitatorsAssigned').replace('{0}', this.activeResident.name)
          )
        }
        return this.getExpression('Alert', 'NoUsers')
      }
      return ''
    },
    title () {
      if (this.isFromKioskDashboard && this.isFromResidentDashboard) {
       return `${this.activeResidentKioskName} / ${this.activeResidentName}`
      } else if (this.isFromResidentDashboard) {
        return this.activeResidentName
      } else if (this.isFromKioskDashboard) {
        return this.activeKioskName
      }
      return 'Users'
    },
    formTitle () {
      return this.editedIndex < 0
        ? this.getExpression('FormTitle', 'CreateUser')
        : this.getExpression('FormTitle', 'EditUser')
    },
    isAdminMode () {
      return this.context === 'admin'
    },
    isSuperAdmin () {
      if (this.isNullOrUndefined(this.profile)) return false
      return this.superAdmins.includes(this.profile.id)
    },
    languagedLanguages () {
      const self = this
      return self.languages.map(l => { return {
        id: l.id,
        description: self.getExpression('Language', l.description)
      }})
    },
    dictionary () { 
      return {
        attributes: {
          email: 'Email'
        },
        custom: {
          email: {
            required: () => this.getExpression('Validation', 'EmailRequired')
          },
          name: {
            required: () => this.getExpression('Validation', 'NameRequired'),
            max: this.getExpression('Validation', 'NameMaxChars').replace('{0}', '50'),
            alpha_spaces: this.getExpression('Validation', 'NameAlphaSpaces')
          },
          nickname: {
            max: this.getExpression('Validation', 'DisplayNameMaxChars').replace('{0}', '20'),
          },
          role: {
            required: () => this.getExpression('Validation', 'RoleRequired')
          },
          domain: {
            required: () => this.getExpression('Validation', 'DomainRequired')
          },
          kiosk: {
            required: () => this.getExpression('Validation', 'KioskRequired')
          },
          resident: {
            required: () => this.getExpression('Validation', 'ResidentRequired')
          }
        }
      }
    },
    isFromResidentDashboard () {
      if (!this.isValidSession) return false
      return this.isResidentManager && this.profile.residentIds.length === 1
        ? this.routeStack.includes('Home')
        : this.routeStack.includes('ResidentDashboard')
    },
    isFromKioskDashboard () {
      if (!this.isValidSession) return false
      return (this.isKioskManager && this.profile.kioskIds.length === 1) || this.isDomainManager
        ? this.routeStack.includes('Home')
        : this.routeStack.includes('KioskDashboard')
    },
    activeKioskTypeId () {
      if (this.isNullOrUndefined(this.activeKioskItem.typeId)) return null
      return this.activeKioskItem.typeId
    },
    activeResidentTypeId () {
      if (this.isNullOrUndefined(this.activeResident.kiosk.typeId)) return null
      return this.activeResident.kiosk.typeId
    },
    activeTypeId () {
      if (this.isFromResidentDashboard) {
        return this.activeResidentTypeId
      } else if (this.isFromKioskDashboard) {
        return this.activeKioskTypeId
      }
      return null
    },
    filteredUsers () {
      if (this.users.length === 0) return []
      const self = this
      var residentIds = []
      var kioskIds = self.kioskId > 0
        ? [self.kioskId]
        : []

      // assemble kioskIds and residentIds
      if (self.kioskTypeId > 0 && self.kioskId === 0 && self.residentId === 0) {
        kioskIds = self.kiosks
          .filter(k => k.typeId === self.kioskTypeId)
          .map(k => k.id)
        if (kioskIds.length === 0) kioskIds = [-1]
        residentIds = self.residents
          .filter(r => kioskIds.includes(r.kioskId))
          .map(r => r.id)
        if (residentIds.length === 0) residentIds = [-1]
      } else if (self.kioskId > 0 && self.residentId === 0) {
        residentIds = self.residents
          .filter(r => kioskIds.includes(r.kioskId))
          .map(r => r.id)
        if (residentIds.length === 0) residentIds = [-1]
        kioskIds = [self.kioskId]
      } else if (self.residentId > 0) {
        residentIds = [self.residentId]
        kioskIds = [-1]
      }

      const users = self.users.filter(u => {
        return (u.functionalRole === self.functionalRole || self.functionalRole === `(${self.getExpression('Label', 'All')})`)
          && ((u.kioskIds?.some(id => kioskIds.includes(id)) || kioskIds.length === 0)
            || (u.residentIds?.some(id => residentIds.includes(id)) || residentIds.length === 0))
      })

      return users
    },
    rolesWithAll () {
      var roles = this.functionalRoles.map(r => r)
      roles.unshift(`(${this.getExpression('Label', 'All')})`)
      if (this.isAdminMode) roles.push(this.functionalRole_NoneAssigned)
      return roles
    },
    headers () {
      var headers = []

      // NON-ADMIN
      if (!this.isAdminMode) {
        headers.push({
          value: 'picture',
          align: 'center',
          width: '5%',
          sortable: false,
          class: 'pr-0'
        })
        headers.push({
          text: this.getExpression('Label', 'Name'),
          value: 'name',
          align: 'left',
          width: this.showAssignedResidents
            ? '30%'
            : '40%',
          class: 'pr-0'
        })
        headers.push({
          text: this.getExpression('Label', 'Email'),
          value: 'email',
          align: 'left',
          width: this.showAssignedResidents
            ? '30%'
            : '35%',
          class: 'pr-0'
        })
        headers.push({
          text: this.getExpression('Label', 'Role'),
          value: 'functionalRole',
          align: 'center',
          width: this.showAssignedResidents
            ? '15%'
            : this.canDelete ? '20%' : '25%',
          class: 'pr-0'
        })
        if (this.showAssignedResidents) {
          headers.push({
            text: this.getExpression('Label', 'Residents'),
            value: 'assignedResidents',
            width: '20%',
            class: 'pr-0',
            align: 'left'
          })
        }
        if (this.canDelete) {
          headers.push({
            value: 'action',
            sortable: false,
            width: '5%',
            align: 'left'
          })
      }

      // ADMIN
      } else {
        headers.push({
          value: 'picture',
          align: 'center',
          width: '5%',
          sortable: false,
          class: 'pr-0'
        })
        headers.push({
          text: this.getExpression('Label', 'Name'),
          value: 'name',
          align: 'left',
          width: '10%',
          class: 'pr-0'
        })
        headers.push({
          text: this.getExpression('Label', 'Email'),
          value: 'email',
          align: 'left',
          width: '15%',
          class: 'pr-0'
        })
        headers.push({
          text: this.getExpression('Label', 'Role'),
          value: 'functionalRole',
          align: 'center',
          width: '5%',
          class: 'pr-0'
        })
        headers.push({
          text: this.getExpression('Label', 'Domains'),
          value: 'assignedDomains',
          width: '15%',
          class: 'pr-0',
          align: 'left'
        })
        headers.push({
          text: this.getExpression('Label', 'Kiosks'),
          value: 'assignedKiosks',
          width: '15%',
          class: 'pr-0',
          align: 'left'
        })
        headers.push({
          text: this.getExpression('Label', 'Residents'),
          value: 'assignedResidents',
          width: '15%',
          class: 'pr-0',
          align: 'left'
        })
        headers.push({
          text: this.getExpression('Label', 'LastLogin'),
          value: 'lastLogin',
          width: '15%',
          class: 'pr-0',
          align: 'left'
        })
        headers.push({
          value: 'action',
          sortable: false,
          width: '5%',
          align: 'left'
        })
      }

      return headers
    },
    activeKioskName () {
      if (this.isNullOrUndefined(this.activeKioskItem)) return null
      return this.activeKioskItem.serialNumber
    },
    activeResidentName () {
      if (this.isNullOrUndefined(this.activeResident)) return null
      return this.activeResident.name
    },
    activeResidentKioskName () {
      if (this.isNullOrUndefined(this.activeResidentKioskItem)) return null
      return this.activeResidentKioskItem.serialNumber
    },
    functionalRolesForEdit () {
      const self = this

      var functionalRoles = self.functionalRoles.map(fr => fr)
    
      if (this.isSuperAdmin && this.isAdminMode) {
        functionalRoles.unshift(self.getExpression('FunctionalRole', 'Admin'))
        return functionalRoles
      }

      // assemble functional roles to display (signed on user usually has an extra one)
      if (self.isSignedOnUser(self.editedUser)) {
        if (!functionalRoles.includes(self.editedUser.functionalRole)) {
          functionalRoles.push(self.editedUser.functionalRole)
        }
      } else {
        functionalRoles = self.getValidatedFunctionalRoles(functionalRoles)
      }

      return functionalRoles
    },
    unblockSnackbarText () {
      return `${this.editedUser.name} has been unblocked`
    },
    residentInfoText () {
      var text = ''
      const self = this
      if ((self.editedUser.residentIds ?? []).length === 0) return

      const residents = self.selectorResidents.filter(r => self.editedUser.residentIds.includes(r.id))

      residents.forEach(r => {
        text = text + `${r.name} - ${r.kiosk}</br>`
      })

      return text
    },
    showResidentTooltipButton () {
      return (this.editedUser.residentIds ?? []).length > 0
        && this.isAdminMode
    },

    // custom selectors
    selectorDomains () {
      const self = this

      // get the facilities of all the kiosks that the
      // user has assigned to them and filter their
      // associated domains out of the list
      const unavailableFacilitiyIds = self.kiosks
        .filter(k => (self.editedUser.kioskIds ?? []).includes(k.id))
        .map(k => k.facilityId)

      const domains = self.domains
        .filter(d => (d.facilityIds ?? [])
          .filter(fid => unavailableFacilitiyIds
            .includes(fid)).length === 0
        )
        .map(d => { return {
          id: d.id,
          name: d.name,
          organization: d.organizationName
        }
      })

      return domains
    },
    selectorKiosks () {
      const self = this

      // if a domain was assigned then filter out its kiosks
      var domainIds = []
      if (self.editedUser.domainIds !== null) {
        domainIds = self.editedUser.domainIds.length > 0
         ? self.editedUser.domainIds
         : []
      }

      var unavailableFacilityIds = []
      if (domainIds.length > 0) {
        const domains = self.domains.filter(d => domainIds.includes(d.id))
        unavailableFacilityIds = domains.flatMap(d => d.facilityIds)
      }

      const filteredKiosks = self.kiosks
        .filter(k => !unavailableFacilityIds.includes(k.facilityId))
        .map(k => { 
          var facility = null
          if (self.facilities !== null) {
            const facilities = self.facilities.filter(f => f.id === k.facilityId)
            facility = facilities.length > 0
              ? facilities[0]
              : null
          }

          return {
            id: k.id,
            serialNumber: k.serialNumber,
            facility: facility !== null
              ? facility.name
              : null,
            domain: k.domainName,
            organization: k.organizationName,
            description: k.description,
            typeId: k.typeId,
          }
      })

      return filteredKiosks
    },
    selectorResidents () {
      if (this.kiosks.length === 0) return

      const self = this
      var availableKiosks = self.kiosks

      const domainIds = self.editedUser.domainIds ?? []
      const kioskIds = self.editedUser.kioskIds ?? []

      // if either domain(s) or kiosks(s) are assigned
      // derive a list of eligible kiosks to filter the residents by
      if (domainIds.length > 0 || kioskIds > 0) {

        availableKiosks = []
        
        // if domain(s) assigned
        if (domainIds.length > 0) {
          const facilityIds = self.domains
            .filter(k => (domainIds ?? []).includes(k.id))
            .map(d => d.facilityIds)
            .flat()

            const domainKiosks = self.kiosks
              .filter(k => facilityIds
              .includes(k.facilityId))

            availableKiosks = domainKiosks

        // if kiosk(s) assigned
        } if (kioskIds.length > 0) {
          const kiosks = self.kiosks
            .filter(k => kioskIds.includes(k.id) 
              && availableKiosks.filter(ak => ak.id === k.id).length === 0)

          if (availableKiosks.length > 0) {
            kiosks.forEach(k => availableKiosks.push(k))
          }
          else availableKiosks = kiosks
        }
      }

      return self.residents
        .filter(r => availableKiosks
          .filter(k => k.id === r.kioskId).length > 0
        )
        .map(r => { 
          const kiosk = availableKiosks.filter(k => k.id === r.kioskId)[0]
          return {
            id: r.id,
            name: r.name,
            kioskTypeId: kiosk.typeId,
            kiosk: kiosk.serialNumber,
            picture: null
          }
        })
    },

    // default collections
    defaultResidents () {
      const self = this
      return self.residents.filter(r => {
        return self.editedUser.residentIds?.includes(r.id)
      })
    },

    // rules
    showPassword () {
      return this.editedIndex < 0 && this.isAdmin
    },
    domainsRule () {
      return this.isDomainRequired ? 'required' : ''
    },
    isDomainRequired () {
      return (
        this.editedUser.functionalRole === this.getExpression('FunctionalRole', 'DomainManager')
      )
    },
    kiosksRule () {
      return this.isKioskRequired ? 'required' : ''
    },
    isKioskRequired () {
      return (
        this.editedUser.functionalRole === this.getExpression('FunctionalRole', 'KioskManager')
      )
    },
    residentsRule () {
      return this.isResidentRequired ? 'required' : ''
    },
    isResidentRequired () {
      return (
        this.editedUser.functionalRole === this.getExpression('FunctionalRole', 'VirtualVisitor') ||
        this.editedUser.functionalRole === this.getExpression('FunctionalRole', 'FamilyFacilitator')
      )
    },
    showDomains () {
      if (!this.isAdminMode) return false
      return (
        this.editedUser.functionalRole === this.getExpression('FunctionalRole', 'Admin') ||
        this.editedUser.functionalRole === this.getExpression('FunctionalRole', 'DomainManager')
      )
    },
    showKiosks () {
      if (!this.isAdminMode) return false
      return (
        this.editedUser.functionalRole === this.getExpression('FunctionalRole', 'Admin') ||
        this.editedUser.functionalRole === this.getExpression('FunctionalRole', 'KioskManager') ||
        this.editedUser.functionalRole === this.getExpression('FunctionalRole', 'DomainManager')
      )
    },
    showResidents () {
      if (!this.isAdminMode) return false
      return this.editedUser.functionalRole !== null
        && this.editedUser.functionalRole !== this.functionalRole_NoneAssigned
    },
    showDefaultResident () {
      const ids = this.editedUser.residentIds ?? []
      const show = ids.length > 1
        && this.showResidents
        && (this.activeTypeId === this.kioskTypeId_Genie || this.isAdminMode)

        return show
    },
    hideRole() {
      if (this.isAdminMode || this.canChangeRole) 
        return false

      return (this.isFromKioskDashboard 
        || this.functionalRolesForEdit.length === 1 
        || this.isSignedOnUser(this.editedUser)
        || this.functionalRolesForEdit.length <= 1)
    },
    allowMultipleDomains () {
      return true
    },
    allowMultipleKiosks () {
      return this.isAdmin
    },
    hideRoleFilter () {
      return !this.isAdminMode
    },
    showResendInvitation () {
      return this.editedIndex >= 0 && !this.editedUser.isEmailVerified && !this.isSignedOnUser(this.editedUser)
    },
    disableResidentFilter () {
      return this.kioskId === 0 || this.isLoading 
    },
    disableKioskFilter () {
      return this.isLoading || (this.selectorKiosks ?? []).length === 0
    },
    disableResidentSelector () {
      return (this.selectorResidents ?? []).length === 0
    },

    // styles
    dialogWidth () {
      return this.profile.languageId === LanguageId.English
        || this.editedIndex < 0
        ? '380px'
        : '480px'
    },
    searchColWidth () {
      return this.isAdminMode
        ? '12'
        : this.hideRoleFilter ? '12' : '9'
    },
    titleClass () {
      return this.isSmallDevice
        ? 'body-2'
        : ''
    }
  },
  methods: {
    loadData () {
      if (!this.isValidSession) return
      const self = this
      self.isLoading = true

      self.getUsers().then(data => {
        if (data.errorSeverity !== null) {
          self.handleError(data)
        } else {
          // store lists
          self.residents = data.residents
          self.kiosks = data.kiosks
          self.facilities = data.facilities
          self.domains = data.domains

          // kiosk combobox
          if (self.kiosksForCombo.length === 0) {
            self.loadKioskCombo()
          }

          self.users = data.users
            .map(u => { return {
              id: u.id,
              defaultResidentId: u.defaultResidentId,
              email: u.email,
              functionalRole: this.getFunctionalRole(u),
              domainIds: u.domainIds,
              facilityIds: u.facilityIds,
              kioskIds: u.kioskIds,
              residentIds: u.residentIds,
              name: u.name,
              nickname: u.nickname,
              password: u.password,
              languageId: u.languageId,
              picture: u.picture,
              role: u.role,
              isEmailVerified: null,
              lastLogin: null,
              lastPasswordReset: null,
              assignedDomains: this.getAssignedDomains(u),
              assignedKiosks: this.getAssignedKiosks(u),
              assignedResidents: this.getAssignedResidents(u),
              createDate: u.createDate
            }})

          self.kioskTypesForFilter = [
            { id: 0, name: `(${self.getExpression('Label', 'All')})` }, 
            { id: KioskTypeId.Abby, name: 'Abby' },
            { id: KioskTypeId.Genie, name: 'Genie' }]

          self.functionalRole = `(${self.getExpression('Label', 'All')})`
          self.populateExtraFields()

          self.isLoading = false
        }
      })
    },

    getUsers () {
      const self = this

      return new Promise(resolve => {
        const profileModel = Object.assign({}, self.profile)

        switch (self.context) {
          case 'kiosk-all':
          case 'kiosk-manager':
          case 'kiosk-resident':
            profileModel.kioskIds = [self.activeKioskItem.id]
            break
          case 'resident':
            profileModel.residentIds = [self.activeResident.id]
            break
        }
        
        profileModel.context = self.context

        appService.getUsersForManager(profileModel).then(data => {
          resolve(data)
        }).catch(e => { self.handleCriticalError(e) })
      })
    },

    loadKioskCombo () {
      const self = this
      
      // derive a collection of kiosks for combobox
      self.kiosksForCombo = (self.kiosks ?? [])
        .filter(k => k.typeId === self.kioskTypeId || self.kioskTypeId === 0)
        .map(k => { return {
          id: k.id,
          name: k.altName
        }})

      // add an option for all
      self.kiosksForCombo.unshift({
        id: 0,
        name: '(All)'
      })
    },

    populateExtraFields () {
      const self = this
      const ids = !self.isAdminMode
        ? self.filteredUsers.map(u => u.id)
        : []

      appService.getUsersExtraFields(ids, self.isAdminMode).then(data => {
        if (this.isNullOrUndefined(data.extraFields)) return

        data.extraFields.forEach(f => {
          const users = self.users.filter(u => u.id === f.id)
          const user = users.length > 0
            ? users[0]
            : null
          if (user !== null) {
            user.isEmailVerified = f.isEmailVerified,
            user.lastLogin = f.lastLogin,
            user.lastPasswordReset = f.lastPasswordReset
          }
        })
      })
    },

    edit (item) {
      const self = this

      self.editedIndex = self.users.findIndex(u => u.id === item.id)
      self.editedUser = Object.assign({}, item)
      self.restoreUser = Object.assign({}, item)

      // we mustn't clobber any of the selected user's residentIds, kioskIds or domainIds
      // that are not in the magagerial scope of the signed-in user (termed 'extraneous' assignments)
      
      // resident
      if (self.editedUser.residentIds !== null) {
        const availableResidentIds = self.residents.map(r => r.id)
        var rids = []
        self.editedUser.residentIds?.forEach(id => { rids.push(id) })
        self.editedUser.residentIds = rids.filter(id => availableResidentIds.includes(id))
        self.editedUser.extraneousResidentIds = rids.filter(id => !availableResidentIds.includes(id))
      }

      // kiosk
      if (self.editedUser.kioskIds !== null) {
        const availableKioskIds = self.kiosks.map(k => k.id)
        var kids = []
        self.editedUser.kioskIds.forEach(id => { kids.push(id) })
        self.editedUser.kioskIds = kids.filter(id => availableKioskIds.includes(id))
        self.editedUser.extraneousKioskIds = kids.filter(id => !availableKioskIds.includes(id))
      }

      // domain
      if (self.editedUser.domainIds !== null) {
        const availableDomainIds = self.domains.map(f => f.id)
        const dids = []
        self.editedUser.domainIds.forEach(id => { dids.push(id) })
        self.editedUser.domainIds = dids.filter(id => availableDomainIds.includes(id))
        self.editedUser.extraneousDomainIds = dids.filter(id => !availableDomainIds.includes(id))
      }
      self.editDialog = true
    },

    remove (item) {
      const self = this
      const index = self.users.findIndex(u => u.id === item.id)
      if (index < 0) return

      const name = self.users[index].name
      self.$confirm.show({
        title: self.getExpression('FormTitle', 'DeleteUser'),
        text: `<b>${name}</b>`,
        formColor: 'red darken-2',
        onConfirm: () => {
          self.isDeleting = true
          const updateByUserId = self.profile.id.replace('auth0|', '')
          appService.deleteUser(item.id, updateByUserId).then(data => {
            self.isDeleting = false
            if (data.errorSeverity > 0) {
              self.handleError(data)
            } else {
              self.users.splice(index, 1)
            }
          }).catch(e => { self.handleCriticalError(e) })
        }
      })
    },

    unlink (item) {
      const self = this
      const index = self.filteredUsers.indexOf(item)
      const name = self.filteredUsers[index].name

      self.$confirm.show({
        title: self.getExpression('FormTitle', 'RemoveUser'),
        text: `<b>${name}</b>`,
        formColor: 'red darken-2',
        onConfirm: () => {
          self.isDeleting = true
          const model = {
            userId: item.id,
            email: item.email,
            performedByUserId: self.profile.id
          }

          if (self.isFromResidentDashboard) {
            model.unlinkType = 1 // resident
            model.entityId = self.activeResident.id
          } else if (self.isFromKioskDashboard) {
            if (item.functionalRole === self.getExpression('FunctionalRole', 'KioskManager')) {
              model.unlinkType = 2 // kiosk
              model.entityId = self.activeKioskItem.id
            } else {
              model.unlinkType = 3 // kiosk residents
              model.entityId = self.activeKioskItem.id
            }
          }

          appService.unlinkUser(model, self.locale).then(data => {
            self.isDeleting = false
            if (data.errorSeverity > 0) {
              self.handleError(data)
            } else {
              self.users.splice(index, 1)
            }
          }).catch(e => { self.handleCriticalError(e) }) 
        }
      })
    },

    save () {
      const self = this
      self.$validator.resume()
      self.$validator.validateAll().then(valid => {
        if (valid) {
          self.isSaving = true

          // edit
          const editIndex = self.editedIndex
          if (editIndex > -1) {
            var editedUser = self.users[self.editedIndex]
            Object.assign(editedUser, self.editedUser)
            const editModel = self.getModel(self.editedUser)

            appService.updateUser(self.editedUser.id, editModel, self.locale).then(data => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
                Object.assign(self.users[self.editedIndex], self.restoreUser)
              } else {
                self.closeDialog()
                if (data.isEmailSent) self.invitationSnackbar = true
                self.users[editIndex].assignedDomains = self.getAssignedDomains(data.user)
                self.users[editIndex].assignedKiosks = self.getAssignedKiosks(data.user)
                self.users[editIndex].assignedResidents = self.getAssignedResidents(data.user)
              }
              self.isSaving = false
              self.$validator.pause()
            }).catch(e => { self.handleCriticalError(e) })
            
          // create
          } else {
            const addModel = self.getModel(self.editedUser)
            appService.createUserFromManager(addModel, self.locale).then(data => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
              } else {
                self.closeDialog()
                self.editedUser.id = data.user.id
                self.editedUser.picture = self.getPicture(data.user.picture)
                self.editedUser.assignedDomains = self.getAssignedDomains(data.user)
                self.editedUser.assignedKiosks = self.getAssignedKiosks(data.user)
                self.editedUser.assignedResidents = self.getAssignedResidents(data.user)
                self.users.unshift(self.editedUser)
                if (data.isEmailSent) self.invitationSnackbar = true
              }
              self.isSaving = false
              self.$validator.pause()
            }).catch(e => { self.handleCriticalError(e) })
          }
        }
      })
    },

    getModel (u) {
      const self = this
      const isNewUser = self.editedIndex < 0

      // password (if new)
      const password = isNewUser
        ? self.fixedPassword
        : null

      const name = u.name.trim()
      const nicknameSafe = u.nickname !== null
        ? u.nickname.length > 0
           ? u.nickname.trim()
           : ''
        : ''

      // derive nickname from full name
      const nickname = (nicknameSafe.length === 0 && name !== null)
        ? name.trim().split(' ')[0].substring(0, 20)
        : nicknameSafe

      // language
      const languageId = u.languageId === LanguageId.English
        ? null
        : u.languageId

      // role
      const role = self.getSelectedRole(u)

      // ---- RESIDENTS ---- //   
      var residentIds = (self.editedUser.residentIds ?? [])
        .concat(self.editedUser.extraneousResidentIds ?? [])

      // elimitate invalid residentIds
      if (self.context === 'admin') {
        residentIds = self.selectorResidents
          .filter(r => residentIds.includes(r.id))
          .map(r => r.id)
      }

      // if default resident is no longer valid, null it out
      if (u.defaultResidentId !== null && !residentIds.includes(u.defaultResidentId))
        u.defaultResidentId = null

      const defaultResidentId = residentIds.length > 1
        ? u.defaultResidentId === null
          ? residentIds[0]
          : u.defaultResidentId
        : null

      // ---- KIOSKS ---- //
      var kioskIds = (self.editedUser.kioskIds ?? [])
        .concat(self.editedUser.extraneousKioskIds ?? [])

      // elimitate invalid kioskIds
      if (self.context === 'admin') {
        kioskIds = self.selectorKiosks
          .filter(k => kioskIds.includes(k.id))
          .map(k => k.id)
      }

      // ---- DOMAINS ---- //
      const domainIds = (self.editedUser.domainIds ?? [])
        .concat(self.editedUser.extraneousDomainIds ?? [])

      // user who performed this action
      var updateByUserId = self.profile.id

      // return the model
      return {
        id: u.id,
        email: u.email,
        name: name,
        password: password,
        nickname: nickname,
        languageId: languageId,
        role: role,
        residentIds: residentIds.length > 0 ? residentIds : null,
        defaultResidentId: defaultResidentId,
        kioskIds:  kioskIds.length > 0 ? kioskIds : null,
        domainIds: domainIds.length > 0 ? domainIds : null,
        updateByUserId: updateByUserId,
        createDate: u.createDate ?? new Date(),
      }
    },

    closeDialog () {
      this.editDialog = false
      this.editedIndex = -1
      setTimeout(() => {
        this.resetEditedUser()
      }, 300)
    },

    getValidatedFunctionalRoles (roles) {
      const self = this
      var result = roles.map(r => r)

      // remove the signed on user role if it exists in the array
      const users = self.users.filter(u => {
        return u.id === this.profile.id
      })
      const signedOnUserRole = users.length > 0 ? users[0].functionalRole : null
      const index = result.indexOf(signedOnUserRole)
      if (index >= 0) {
        result.splice(index, 1)
      }
      return result
    },

    resetEditedUser () {
      this.editedUser = Object.assign({}, this.defaultUser)
      this.editedIndex = -1
      this.$validator.reset()
    },

    refreshExtraFields (value) {
      const self = this
      if (!self.isAdminMode) return
      if (value === undefined) return

      const users = self.filteredUsers.filter(u => u.id === value.id)
      const user = users.length === 1
        ? users[0]
        : null

      // don't send request if already verified
      if (user.isEmailVerified) return

      appService.getUsersExtraFields([user.id]).then(data => {
        if (this.isNullOrUndefined(data.extraFields)) return

        const ef = data.extraFields?.length === 1 
          ? data.extraFields[0]
          : null
          
          if (ef !== null) {
            user.isEmailVerified = ef.isEmailVerified,
            user.lastLogin = ef.lastLogin,
            user.lastPasswordReset = ef.lastPasswordReset
          }
      })
    },

    showSummary () {
      const self = this
      self.$alert.show({
        title: self.editedUser.functionalRole === self.getExpression('FunctionalRole', 'KioskManager')
          ? self.getExpression('FormTitle', 'ManagerCreated')
          : self.getExpression('FormTitle', 'ContactConnected'),
        text:  `${self.getExpression('Alert', 'ContactAddConfirmation1')}</br></br>
                ${self.getExpression('Alert', 'ContactAddConfirmation2')
                  .replace('{0}', self.editedUser.functionalRole)}`,
          formColor: 'success',
          icon: 'done'
      })
    },
    
    onCopy () {
      this.copySnackbar = true
    },

    customFilter (value, search, item) {
      return (
        (value != null &&
          search != null &&
          item.name
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1) ||
          item.email
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          item.assignedResidents.map(r => r.name).join(',')
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          item.assignedKiosks.map(k => k.name).join(',')
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          item.assignedDomains.map(d => d.name).join(',')
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1
      )
    },

    getAssignedDomains (item) {
      if (this.isNullOrUndefined(item.domainIds)) return []
      return this.domains.filter(f => item.domainIds.includes(f.id))
    }, 
    
    getAssignedKiosks (item) {
      if (item.kioskIds === null) return []
      return this.kiosks.filter(k => item.kioskIds.includes(k.id))
    },    

    getAssignedResidents (item) {
      if (item.residentIds === null) return []
      return this.residents.filter(r => item.residentIds.includes(r.id))
    },

    getFunctionalRole (item) {
      return this.isOrphaned(item)
        ? this.functionalRole_NoneAssigned
        : this.getExpression('FunctionalRole', item.functionalRole.replace(/ /g,''))
    },

    getRoleCode (item) {
      if (this.isNullOrUndefined(item.functionalRole)) return

      if (item.functionalRole === this.getExpression('FunctionalRole', 'FamilyFacilitator'))
        return 'FF'
      else if (item.functionalRole === this.getExpression('FunctionalRole', 'KioskManager'))
        return 'KM'
      else if (item.functionalRole === this.getExpression('FunctionalRole', 'DomainManager'))
        return 'DM'
      else if (item.functionalRole === this.getExpression('FunctionalRole', 'VirtualVisitor')) return 'VV'
      else if (item.functionalRole === this.getExpression('FunctionalRole', 'Admin')) return 'A'
      else if (item.functionalRole === this.functionalRole_NoneAssigned) return 'NA'
    },

    getRoleColor (item) {
      if (this.isNullOrUndefined(item.functionalRole)) return

      if (item.functionalRole === this.getExpression('FunctionalRole', 'Admin')) {
        return this.appColor_RoleAdmin
      } else if (item.functionalRole === this.getExpression('FunctionalRole', 'DomainManager')) {
        return this.appColor_RoleDomainManager
      } else if (item.functionalRole === this.getExpression('FunctionalRole', 'KioskManager')) {
        return this.appColor_RoleKioskManager
      } else if (item.functionalRole === this.getExpression('FunctionalRole', 'FamilyFacilitator')) {
        return this.appColor_RoleResidentManager
      } else if (item.functionalRole === this.getExpression('FunctionalRole', 'VirtualVisitor')) {
        return this.appColor_RoleGuest
      }
    },

    isOrphaned (item) {
      return (item.residentIds === null && item.kioskIds === null && item.domainIds === null 
        && item.functionalRole !== this.getExpression('FunctionalRole', 'Admin'))
    },

    isSignedOnUser (item) {
      return item.id === this.profile.id
    },

    disableEdit (item) {
      if (this.isSignedOnUser(item)) return false
      if (this.isSuperAdmin) return false
      if (item.functionalRole === this.getExpression('FunctionalRole', 'Admin')) return true
      if (this.isAdminMode) return false

      // an admin in any other mode than AdminMode should mimic the signed on user
      const isResidentManager = this.isResidentManager || this.isAdmin
      const isKioskManager = this.isKioskManager || this.isAdmin
      const isDomainManager = this.isDomainManager || this.isAdmin

      if (isDomainManager)
        return item.functionalRole === this.getExpression('FunctionalRole', 'DomainManager')
       
       if (isKioskManager)
          return item.functionalRole === this.getExpression('FunctionalRole', 'KioskManager') || 
            item.functionalRole === this.getExpression('FunctionalRole', 'DomainManager')
          
      if (isResidentManager)
        return item.functionalRole !== this.getExpression('FunctionalRole', 'VirtualVisitor')
        
      return true
    },

    disableDelete (item) {
      return (this.disableEdit(item) && !this.isSuperAdmin) || this.isSignedOnUser(item)
    },

    getPicture (picture) {
      if (this.isNullOrUndefined(picture)) return this.imageUri_NoAvatar

      return picture.indexOf('https://s.gravatar.com/') >= 0
        ? this.imageUri_NoAvatar
        : picture
    },

    getSelectedRole (user) {
      var role = this.role_Guest
      if (user.functionalRole === this.getExpression('FunctionalRole', 'Admin')) {
        role = this.role_Admin
      } else if (
        user.functionalRole === this.getExpression('FunctionalRole', 'FamilyFacilitator') ||
        user.functionalRole === this.getExpression('FunctionalRole', 'KioskManager') ||
        user.functionalRole === this.getExpression('FunctionalRole', 'DomainManager')
      ) {
        role = this.role_Manager
      }
      return role
    },
    
    resendInvitationEmail () {
      const self = this

      self.$confirm.show({
        title: 'Resend Invitation?',
        text: `Recipient: <b>${self.editedUser.email}</b>`,
        formColor: 'info',
        width: 300,
        onConfirm: () => {
          appService.sendChangePasswordEmail(this.editedUser.email).then(() => {
            self.invitationSnackbar = true
          }).catch(e => { self.handleCriticalError(e) })
        }
      })
    },

    unblock () {
      const self = this

      self.$confirm.show({
        title: 'Unblock User?',
        text: `Unblock the user with email <b>${self.editedUser.email}</b>?`,
        formColor: 'info',
        width: 300,
        onConfirm: () => {
          appService.unblockUser(self.editedUser.id).then(() => {
            self.unblockSnackbar = true
          }).catch(e => { self.handleCriticalError(e) })
        }
      })
    },

    filterPanelClick () {
      const self = this
      // panel will be null or undefined when expanding
      if (self.isNullOrUndefined(self.panel)) {
        // give the kiosk combo a moment to render before initializing it
        setTimeout(() => { self.kioskId = 0 }, 300)
      }
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
          ? this.getExpression('FormTitle', 'ValidationFailed')
          : this.getExpression('FormTitle', 'InternalError'),
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  created() {
    this.loadData()
  },
  beforeDestroy() {
    this.$validator.pause()
  },
  mounted() {
    this.$validator.localize('en', this.dictionary)
    this.$validator.reset()
  },
  watch: {
    editDialog (val) {
      if (val) {
        const self = this
        self.$validator.reset()
        self.$validator.pause()
        if (self.editedIndex < 0) self.resetEditedUser()
        setTimeout(function() {
          if (self.editedIndex < 0) {

            self.editedUser.languageId = LanguageId.English
            if (self.functionalRole !== "(All)") self.editedUser.functionalRole = self.functionalRole

            // set appropriate focus
            self.editedUser.languageId = LanguageId.English
            self.$refs.email.focus()
          } else {
            self.$refs.name.focus()
          }
        }, 300)
      } else {
        this.closeDialog()
      }
    },

    isValidSession (val) {
      if (val === null) return
      this.loadData()
    },

    kioskTypeId (newVal, oldVal) {
      if (oldVal !== newVal) {
        this.loadKioskCombo()
        this.kioskId = 0
      }
    },

    kioskId (newVal, oldVal) {
      if (oldVal !== newVal) {
        if (newVal === null) {
          this.kioskId = 0
          return
        }
        if (newVal > 0) {
          const self = this
          self.residentsForFilter = self.residents
            .filter(r => r.kioskId === newVal)
            .map(r => { return {
              id: r.id,
              name: r.name
            }})

          self.residentsForFilter.unshift({ id: 0, name: `(${self.getExpression('Label', 'All')})` })
          self.residentId = 0
        }
      }
    }
  }
}
</script>

<style>
.truncate {
  max-width: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
